// src/components/exam/charts/OverallPerformanceChart.jsx

import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

const OverallPerformanceChart = ({ data, averageScore, maxScore = 225 }) => {
  // Custom tooltip - Show RAW SCORES
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const score = payload[0].value;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-800">
            Exam #{payload[0].payload.examNumber}
          </p>
          <p className="text-xs text-gray-600">{payload[0].payload.date}</p>
          <p className="text-sm font-bold text-blue-600">
            Score: {score}/{maxScore}
          </p>
        </div>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-96 flex items-center justify-center text-gray-500">
        No exam data available
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Chart */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 Score Progression</h3>
        <p className="text-sm text-gray-600 mb-4">Track your performance over time</p>

        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="examNumber"
              label={{ value: 'Exam Number', position: 'insideBottomRight', offset: -5 }}
            />
            <YAxis
              label={{ value: `Score (out of ${maxScore})`, angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <ReferenceLine
              y={averageScore}
              stroke="#6b7280"
              strokeDasharray="5 5"
              label={{ value: `Avg: ${Math.round(averageScore)}/${maxScore}`, position: 'right' }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#3b82f6"
              dot={{ fill: '#3b82f6', r: 4 }}
              activeDot={{ r: 6 }}
              name="Your Score"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Stats Cards - Show RAW SCORES */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-xs font-medium text-gray-600">Total Exams</p>
          <p className="text-2xl font-bold text-gray-900">{data.length}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <p className="text-xs font-medium text-gray-600">Average</p>
          <p className="text-2xl font-bold text-gray-900">
            {Math.round(averageScore)}/{maxScore}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-xs font-medium text-gray-600">Latest</p>
          <p className="text-2xl font-bold text-gray-900">
            {data.length > 0 ? data[data.length - 1].score : 0}/{maxScore}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OverallPerformanceChart;
